import { listingUtils_getDistanceToUserPoint } from 'app/shared/utils/listingUtils';
import { areaUtils_forTypeOf } from 'app/shared/utils/areaUtils';

export type School = {
  id?: string;
  name: string;
  type: string;
  isPrivate?: boolean;
  lowGrade?: string;
  highGrade?: string;
  rating?: number;
  uriV2: string;
  geo?: { lat: number; lon: number };
};

export type District = {
  id?: string;
  name: string;
  type: string;
  uriV2: string;
};

export type NearbySchool = School & { distance: number };

export type SchoolTypeId = 'all' | 'primaryschool' | 'middleschool' | 'highschool' | 'mixedschool' | 'university';

export const INITIAL_VISIBLE_COUNT = 4;
export const NEAREST_SCHOOLS_LIMIT = 10;

const SCHOOL_TYPE_ORDER: Array<Exclude<SchoolTypeId, 'all'>> = [
  'primaryschool',
  'middleschool',
  'highschool',
  'mixedschool',
  'university',
];

const SCHOOL_TYPE_LABELS: Record<Exclude<SchoolTypeId, 'all'>, { singular: string; plural: string }> = {
  primaryschool: { singular: 'primary school', plural: 'primary schools' },
  middleschool: { singular: 'middle school', plural: 'middle schools' },
  highschool: { singular: 'high school', plural: 'high schools' },
  mixedschool: { singular: 'mixed school', plural: 'mixed schools' },
  university: { singular: 'university', plural: 'universities' },
};

export const computeNearestSchools = (
  schools: Array<School>,
  origin: { lat: number; lon: number },
  limit: number = NEAREST_SCHOOLS_LIMIT,
): Array<NearbySchool> => {
  return schools
    .filter((school) => school.geo && typeof school.geo.lat === 'number' && typeof school.geo.lon === 'number')
    .map((school) => ({
      ...school,
      distance: Number(
        listingUtils_getDistanceToUserPoint({
          originLat: origin.lat,
          originLon: origin.lon,
          lat0: school.geo!.lat,
          lon0: school.geo!.lon,
        }).toFixed(1),
      ),
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
};

export const groupSchoolsByType = (
  schools: Array<NearbySchool>,
): Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>> => {
  const grouped: Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>> = {
    primaryschool: [],
    middleschool: [],
    highschool: [],
    mixedschool: [],
    university: [],
  };
  for (const school of schools) {
    if (school.type in grouped) {
      grouped[school.type as Exclude<SchoolTypeId, 'all'>].push(school);
    }
  }
  return grouped;
};

/**
 * Produce the "All" tab ordering used by the legacy implementation:
 * first the nearest school of each type (in type order), then the remaining
 * schools (starting with the second-nearest of each type). Preserves the
 * "one of each kind first" feel from the old UI.
 */
export const buildAllTabOrdering = (
  grouped: Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>>,
): { schools: Array<NearbySchool>; firstRestIndex: number } => {
  const firsts: Array<NearbySchool> = [];
  const rest: Array<NearbySchool> = [];
  for (const type of SCHOOL_TYPE_ORDER) {
    const group = grouped[type];
    if (group.length > 0) firsts.push(group[0]);
    for (let i = 1; i < group.length; i++) rest.push(group[i]);
  }
  return { schools: [...firsts, ...rest], firstRestIndex: firsts.length };
};

export const getSchoolTypeLabel = (school: Pick<School, 'type' | 'isPrivate'>): string => {
  if (school.type === 'university') return 'College';
  if (school.isPrivate) return 'Private';
  return 'Public';
};

export const getGradeLabel = (lowGrade?: string, highGrade?: string): string | null => {
  if (!lowGrade || lowGrade === 'M') return null;
  const low = lowGrade === 'KG' ? 'K' : lowGrade;
  return `Grades ${low} - ${highGrade ?? ''}`;
};

export const getShowingMessage = (tabId: SchoolTypeId, totalSchools: number, allVisibleCount: number): string => {
  if (totalSchools === 0) return '';
  const initialCount = tabId === 'all' ? allVisibleCount : Math.min(totalSchools, INITIAL_VISIBLE_COUNT);
  if (tabId === 'all') {
    const schoolWord = initialCount === 1 ? 'school' : 'schools';
    return `Showing ${initialCount} of ${totalSchools} ${schoolWord}.`;
  }
  const labels = SCHOOL_TYPE_LABELS[tabId];
  const word = initialCount === 1 ? labels.singular : labels.plural;
  return `Showing ${initialCount} of ${totalSchools} ${word}.`;
};

export const lookupDistrictTypeLabel = (type: string): string | undefined => {
  const entry = (areaUtils_forTypeOf as Record<string, { display?: { specific?: string } } | undefined>)[type];
  return entry?.display?.specific;
};

export const renderDistrictJoiner = (index: number, total: number): string => {
  const isLast = index === total - 1;
  const isSecondToLast = index === total - 2;
  if (isLast) return '';
  if (total === 2) return ' and ';
  if (total > 2 && isSecondToLast) return ', and ';
  return ', ';
};
